/**
 * Onboarding.jsx - Premium Split-Screen Design
 * ============================================
 * Split layout: Left form panel, Right luxury image
 * Step 1: Welcome + Business Type (simplified 3 options)
 * Step 2: Location 
 * Step 3: Instagram
 * Step 4: Success + Redirect
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import supabase from '../lib/supabase';
import authService from '../services/authService';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  ArrowLeft, 
  User,
  MapPin, 
  Instagram,
  Check,
  Sparkles,
  Loader2,
  ChevronDown,
  Briefcase,
  Building2,
  Home
} from 'lucide-react';
import MapboxLocationSelector from '../components/MapboxLocationSelector';

// SIMPLIFIED BUSINESS TYPES (3 options only)
const BUSINESS_TYPES = [
  { 
    value: 'architect_designer', 
    label: 'Architetto / Designer',
    description: 'Professionista del design e architettura',
    icon: Briefcase
  },
  { 
    value: 'company_supplier', 
    label: 'Azienda / Fornitore',
    description: 'Showroom, distributore, produttore',
    icon: Building2
  },
  { 
    value: 'private_client', 
    label: 'Cliente Privato',
    description: 'Ristrutturazione casa, progetto personale',
    icon: Home
  }
];

// Map old role_professional values to new business_type
const LEGACY_ROLE_MAPPING = {
  'architect': 'architect_designer',
  'interior_designer': 'architect_designer',
  'designer': 'architect_designer',
  'advisor': 'architect_designer',
  'studio': 'architect_designer',
  'developer': 'architect_designer',
  'contractor': 'architect_designer',
  'company': 'company_supplier',
  'supplier': 'company_supplier',
  'retail': 'company_supplier',
  'factory': 'company_supplier',
  'fabricator': 'company_supplier',
  'real_estate': 'company_supplier',
  'private_client': 'private_client',
  'private': 'private_client'
};

// Premium background images for right panel
const STEP_IMAGES = [
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=85', // Luxury kitchen marble
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=85', // Elegant interior
  'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&q=85', // Modern bathroom
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85', // Luxury villa
];

// Step Indicator Component
const StepIndicator = ({ currentStep, totalSteps = 3 }) => (
  <div className="flex items-center justify-center gap-3">
    {Array.from({ length: totalSteps }, (_, i) => (
      <React.Fragment key={i}>
        <div 
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-500 ${
            i < currentStep 
              ? 'bg-[var(--gold)] text-[#0a0a0f]' 
              : i === currentStep 
                ? 'bg-[var(--gold)] text-[#0a0a0f] ring-4 ring-[var(--gold)]/30' 
                : 'bg-[#1a1a2e] text-white/40 border border-white/10'
          }`}
        >
          {i < currentStep ? <Check size={16} strokeWidth={3} /> : i + 1}
        </div>
        {i < totalSteps - 1 && (
          <div className={`w-16 h-1 rounded-full transition-all duration-500 ${
            i < currentStep ? 'bg-[var(--gold)]' : 'bg-white/10'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// SR Logo
const SRLogo = () => (
  <div className="mb-8">
    <div className="flex items-center gap-3">
      <div className="text-4xl font-bold tracking-tight">
        <span className="text-white">SLAB</span>
        <span className="text-[var(--gold)]">REALITY</span>
      </div>
    </div>
    <p className="text-white/40 text-xs tracking-[0.25em] uppercase mt-1">
      The Real Slab. In Your Space.
    </p>
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, profile, refreshProfile, loading: authLoading } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [stepLoaded, setStepLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isCompleting, setIsCompleting] = useState(false);
  
  const [formData, setFormData] = useState({
    business_type: '',
    location: null,
    instagram_url: ''
  });
  
  const TOTAL_STEPS = 4;
  
  // Initialize from profile (with legacy mapping)
  useEffect(() => {
    if (profile && !stepLoaded) {
      // Map legacy role_professional to new business_type
      let businessType = profile.business_type || '';
      if (!businessType && profile.role_professional) {
        businessType = LEGACY_ROLE_MAPPING[profile.role_professional] || '';
      }
      
      console.log('[ONBOARDING] Initializing from profile', {
        business_type: businessType,
        legacy_role: profile.role_professional,
        city: profile.city,
        onboarding_step: profile.onboarding_step
      });
      
      setFormData(prev => ({
        ...prev,
        business_type: businessType,
        instagram_url: profile.instagram_url || '',
        location: profile.city ? {
          city: profile.city,
          city_id: profile.city_id || '',
          state_name: profile.state_name || '',
          state_code: profile.state_code || '',
          country_name: profile.country_name || '',
          country_code: profile.country_code || '',
          lat: profile.lat || null,
          lng: profile.lng || null
        } : null
      }));
      
      const savedStep = profile.onboarding_step || 0;
      if (savedStep > 0 && savedStep < TOTAL_STEPS - 1) {
        setCurrentStep(savedStep);
        toast.info(`Riprendi da dove ti eri fermato (step ${savedStep + 1})`);
      }
      
      setStepLoaded(true);
    }
  }, [profile, stepLoaded]);
  
  // Redirect if complete
  useEffect(() => {
    if (!authLoading && profile?.onboarding_completed) {
      console.log('[ONBOARDING] Already completed, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [profile, authLoading, navigate]);
  
  // Redirect if not logged in - wait for auth to initialize
  useEffect(() => {
    // IMPORTANT: Don't redirect during initial loading
    if (authLoading) {
      return;
    }
    
    // Only redirect if auth is definitely initialized AND no user
    if (!user) {
      console.log('[ONBOARDING] No user after auth init, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);
  
  // Countdown for final step
  useEffect(() => {
    if (currentStep === TOTAL_STEPS - 1 && isCompleting) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/dashboard', { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentStep, isCompleting, navigate]);
  
  // Save step progress
  const saveStepProgress = async (step) => {
    if (!user || !supabase) return;
    try {
      await supabase.from('profiles').update({ onboarding_step: step }).eq('id', user.id);
    } catch (e) {
      console.error('[ONBOARDING] Failed to save step:', e);
    }
  };
  
  // Save form data - simplified, direct update
  const saveFormData = async (data) => {
    if (!user || !supabase) return false;
    
    setIsSubmitting(true);
    try {
      const updateData = {};
      
      // Save business_type (new field) AND role_professional (for backward compatibility)
      if (data.business_type) {
        updateData.business_type = data.business_type;
        // Also save to role_professional for backward compatibility
        updateData.role_professional = data.business_type;
      }
      
      if (data.location) {
        updateData.city = data.location.city;
        updateData.city_id = data.location.city_id;
        updateData.state_name = data.location.state_name;
        updateData.state_code = data.location.state_code;
        updateData.country_name = data.location.country_name;
        updateData.country_code = data.location.country_code;
        updateData.lat = data.location.lat;
        updateData.lng = data.location.lng;
      }
      
      if (data.instagram_url !== undefined) {
        updateData.instagram_url = data.instagram_url;
      }
      
      console.log('[ONBOARDING] Saving data:', updateData);
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) {
        console.error('[ONBOARDING] Save error:', error);
        throw error;
      }
      
      console.log('[ONBOARDING] Save successful');
      return true;
    } catch (e) {
      console.error('[ONBOARDING] Save exception:', e);
      toast.error('Errore nel salvataggio');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Complete onboarding - STABLE VERSION with proper async flow
  const completeOnboarding = async () => {
    if (!user || !supabase) {
      console.error('[ONBOARDING] Cannot complete: no user or supabase');
      return;
    }
    
    // Prevent double submission
    if (isSubmitting || isCompleting) {
      console.log('[ONBOARDING] Already submitting/completing, skipping');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Save instagram if provided (async but don't block completion)
      if (formData.instagram_url) {
        try {
          await saveFormData({ instagram_url: formData.instagram_url });
        } catch (e) {
          console.warn('[ONBOARDING] Instagram save failed (non-blocking):', e);
        }
      }
      
      console.log('[ONBOARDING] Completing onboarding for user:', user.id);
      
      // Mark onboarding as complete - CRITICAL OPERATION
      const { error } = await supabase
        .from('profiles')
        .update({
          onboarding_completed: true,
          onboarding_step: TOTAL_STEPS - 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) {
        console.error('[ONBOARDING] Complete error:', error);
        throw error;
      }
      
      console.log('[ONBOARDING] Database updated successfully');
      
      // IMPORTANT: Wait for profile refresh BEFORE starting countdown
      // This ensures the profile state is correct if user refreshes
      try {
        await refreshProfile();
        console.log('[ONBOARDING] Profile refreshed successfully');
      } catch (refreshError) {
        console.warn('[ONBOARDING] Profile refresh failed (non-blocking):', refreshError);
      }
      
      // Only NOW show success and start countdown
      setIsSubmitting(false);
      setIsCompleting(true);
      setCurrentStep(TOTAL_STEPS - 1);
      toast.success('Profilo completato!');
      
    } catch (e) {
      console.error('[ONBOARDING] Complete exception:', e);
      toast.error('Errore nel completamento. Riprova.');
      setIsSubmitting(false);
    }
  };
  
  // Navigation handlers
  const handleNext = async () => {
    if (currentStep === 0 && !formData.business_type) {
      toast.error('Seleziona il tipo di attività');
      return;
    }
    
    if (currentStep === 1 && !formData.location) {
      toast.error('Inserisci la tua posizione');
      return;
    }
    
    if (currentStep === 0) {
      const saved = await saveFormData({ business_type: formData.business_type });
      if (!saved) return;
    } else if (currentStep === 1) {
      const saved = await saveFormData({ location: formData.location });
      if (!saved) return;
    }
    
    const nextStep = currentStep + 1;
    
    if (nextStep === TOTAL_STEPS - 1) {
      await completeOnboarding();
    } else {
      setCurrentStep(nextStep);
      saveStepProgress(nextStep);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Get business type label
  const getBusinessTypeLabel = () => {
    const bt = BUSINESS_TYPES.find(t => t.value === formData.business_type);
    return bt?.label || 'Seleziona';
  };
  
  // Loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[var(--gold)] animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* LEFT PANEL - Form */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen p-6 lg:p-12">
        {/* Logo */}
        <SRLogo />
        
        {/* Step Indicator */}
        <div className="mb-8">
          <StepIndicator currentStep={currentStep} totalSteps={3} />
        </div>
        
        {/* Form Content */}
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            
            {/* Step 0: Welcome + Business Type (Simplified) */}
            {currentStep === 0 && (
              <div className="animate-fade-in">
                <div className="w-16 h-16 rounded-xl bg-[#1a1a2e] border border-white/10 flex items-center justify-center mb-6">
                  <User size={28} className="text-[var(--gold)]" />
                </div>
                
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  Benvenuto in <span className="text-[var(--gold)]">SlabReality</span>
                </h1>
                
                <p className="text-white/50 mb-8 leading-relaxed">
                  Come utilizzerai la piattaforma?
                </p>
                
                {/* Business Type Selection - Card Style */}
                <div className="space-y-3 mb-8">
                  {BUSINESS_TYPES.map(bt => {
                    const Icon = bt.icon;
                    const isSelected = formData.business_type === bt.value;
                    return (
                      <button
                        key={bt.value}
                        onClick={() => setFormData(prev => ({ ...prev, business_type: bt.value }))}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left ${
                          isSelected 
                            ? 'bg-[var(--gold)]/10 border-[var(--gold)] ring-2 ring-[var(--gold)]/30' 
                            : 'bg-[#1a1a2e] border-white/10 hover:border-white/30 hover:bg-[#1a1a2e]/80'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[var(--gold)] text-[#0a0a0f]' : 'bg-[#252538] text-white/60'
                        }`}>
                          <Icon size={24} />
                        </div>
                        <div className="flex-1">
                          <div className={`font-semibold ${isSelected ? 'text-[var(--gold)]' : 'text-white'}`}>
                            {bt.label}
                          </div>
                          <div className="text-white/40 text-sm">
                            {bt.description}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-[var(--gold)] flex items-center justify-center">
                            <Check size={14} className="text-[#0a0a0f]" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Next Button */}
                <button
                  onClick={handleNext}
                  disabled={!formData.business_type || isSubmitting}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[var(--gold)] text-[#0a0a0f] font-semibold text-lg rounded-xl hover:bg-[#d4b896] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[var(--gold)]/20"
                >
                  {isSubmitting ? (
                    <Loader2 size={22} className="animate-spin" />
                  ) : (
                    <>
                      Avanti
                      <ArrowRight size={22} />
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Step 1: Location */}
            {currentStep === 1 && (
              <div className="animate-fade-in">
                <div className="w-16 h-16 rounded-xl bg-[#1a1a2e] border border-white/10 flex items-center justify-center mb-6">
                  <MapPin size={28} className="text-[var(--gold)]" />
                </div>
                
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  Dove <span className="text-[var(--gold)]">ti trovi</span>?
                </h1>
                
                <p className="text-white/50 mb-8 leading-relaxed">
                  La tua posizione ci aiuta a connetterti con fornitori<br />
                  locali e mostrarti materiali disponibili nella tua area.
                </p>
                
                {/* Location Input */}
                <div className="mb-8 relative z-50">
                  <MapboxLocationSelector
                    value={formData.location}
                    onChange={(loc) => setFormData(prev => ({ ...prev, location: loc }))}
                    placeholder="Cerca la tua città..."
                    label="Città o regione"
                    className="[&_input]:!bg-[#1a1a2e] [&_input]:!border-white/20 [&_input]:!rounded-xl [&_input]:!py-4 [&_input]:!text-lg"
                  />
                </div>
                
                {/* Navigation */}
                <div className="flex gap-4">
                  <button
                    onClick={handleBack}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-transparent border border-white/20 text-white rounded-xl hover:bg-white/5 transition-all"
                  >
                    <ArrowLeft size={20} />
                    Indietro
                  </button>
                  
                  <button
                    onClick={handleNext}
                    disabled={!formData.location || isSubmitting}
                    className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-[var(--gold)] text-[#0a0a0f] font-semibold text-lg rounded-xl hover:bg-[#d4b896] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[var(--gold)]/20"
                  >
                    {isSubmitting ? (
                      <Loader2 size={22} className="animate-spin" />
                    ) : (
                      <>
                        Avanti
                        <ArrowRight size={22} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 2: Instagram */}
            {currentStep === 2 && (
              <div className="animate-fade-in">
                <div className="w-16 h-16 rounded-xl bg-[#1a1a2e] border border-white/10 flex items-center justify-center mb-6">
                  <Instagram size={28} className="text-[var(--gold)]" />
                </div>
                
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  Portfolio <span className="text-[var(--gold)]">Instagram</span>
                </h1>
                
                <p className="text-white/50 mb-8 leading-relaxed">
                  Condividi il tuo portfolio per connetterti<br />
                  con altri professionisti e ispirare collaborazioni.
                </p>
                
                {/* Instagram Input */}
                <div className="mb-2">
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Link al profilo Instagram
                  </label>
                  <div className="flex items-center gap-3 bg-[#1a1a2e] border border-white/20 rounded-xl px-5 py-4 focus-within:border-[var(--gold)] focus-within:ring-2 focus-within:ring-[var(--gold)]/20 transition-all">
                    <Instagram size={22} className="text-white/40" />
                    <input
                      type="url"
                      value={formData.instagram_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                      placeholder="https://instagram.com/tuoprofilo"
                      className="flex-1 bg-transparent text-white text-lg placeholder:text-white/30 focus:outline-none"
                    />
                  </div>
                </div>
                
                <p className="text-white/30 text-sm mb-8">
                  Opzionale — puoi aggiungerlo in seguito dalle impostazioni
                </p>
                
                {/* Navigation */}
                <div className="flex gap-4">
                  <button
                    onClick={handleBack}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-transparent border border-white/20 text-white rounded-xl hover:bg-white/5 transition-all"
                  >
                    <ArrowLeft size={20} />
                    Indietro
                  </button>
                  
                  <button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-[var(--gold)] text-[#0a0a0f] font-semibold text-lg rounded-xl hover:bg-[#d4b896] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[var(--gold)]/20"
                  >
                    {isSubmitting ? (
                      <Loader2 size={22} className="animate-spin" />
                    ) : (
                      <>
                        Completa
                        <ArrowRight size={22} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 3: Success */}
            {currentStep === 3 && (
              <div className="animate-fade-in text-center">
                <div className="w-20 h-20 rounded-2xl bg-[var(--gold)] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[var(--gold)]/30">
                  <Check size={40} className="text-[#0a0a0f]" strokeWidth={3} />
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                  Ottimo!
                </h1>
                
                <p className="text-white/50 text-lg mb-8 leading-relaxed">
                  La tua esperienza è stata personalizzata con successo.<br />
                  Sei pronto a trasformare le tue idee in decisioni reali.
                </p>
                
                {/* Loading Bar */}
                <div className="bg-[#1a1a2e] rounded-xl p-4 mb-6 border border-white/10">
                  <div className="flex items-center gap-3">
                    <Sparkles size={20} className="text-[var(--gold)] animate-pulse" />
                    <span className="text-white/70">
                      Preparazione della tua dashboard personalizzata...
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[var(--gold)] rounded-full transition-all duration-1000"
                      style={{ width: `${((3 - countdown) / 3) * 100}%` }}
                    />
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/dashboard', { replace: true })}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[var(--gold)] text-[#0a0a0f] font-semibold text-lg rounded-xl hover:bg-[#d4b896] transition-all shadow-lg shadow-[var(--gold)]/20"
                >
                  Vai alla Dashboard
                  <ArrowRight size={22} />
                </button>
                
                <p className="text-white/30 text-sm mt-4">
                  Reindirizzamento automatico tra {countdown}s...
                </p>
              </div>
            )}
            
          </div>
        </div>
        
        {/* Footer */}
        <div className="pt-6 text-center lg:text-left">
          <p className="text-white/20 text-xs">
            © 2026 SlabReality. Tutti i diritti riservati.
          </p>
        </div>
      </div>
      
      {/* RIGHT PANEL - Luxury Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-all duration-700"
          style={{ 
            backgroundImage: `url(${STEP_IMAGES[currentStep] || STEP_IMAGES[0]})`
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-[#0a0a0f]/40" />
          
          {/* Premium Badge */}
          <div className="absolute bottom-8 right-8">
            <div className="bg-black/50 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Premium Materials</p>
              <p className="text-white font-medium">Natural Stone Collection</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Background Image (subtle) */}
      <div 
        className="lg:hidden fixed inset-0 bg-cover bg-center -z-10 opacity-20"
        style={{ backgroundImage: `url(${STEP_IMAGES[currentStep]})` }}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Onboarding;
